// The KGOB Journal — Article detail page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ARTICLES, ARTICLE_BODIES, CITATIONS_BY_ARTICLE } from './data';
import { Masthead, Colophon, Avatar, ShareRail, Icon } from './components';
import { JsonLd } from '@/hooks/useJsonLd';
import './blog.css';

// Legacy/long slugs from earlier drafts that external audits still reference
const LEGACY_JOURNAL_ALIASES = {
  'ohio-commercial-activity-tax-2024-2026-reform': 'ohio-cat-2026',
};

// ---------- Citation format selector ----------
function CitationFormatPanel({ article }) {
  const [fmt, setFmt] = useState('Bluebook');
  const formats = ['Bluebook', 'APA', 'Chicago', 'MLA'];
  const auth = article.author.name.split(',')[0];
  const lastFirst = (() => {
    const parts = auth.split(' ');
    const last = parts.pop();
    return `${last}, ${parts.join(' ')}`;
  })();
  const d = new Date(article.date);
  const monthLong = d.toLocaleDateString('en-US', { month: 'long' });
  const monthShort = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const yr = d.getFullYear();
  const samples = {
    Bluebook: `${auth}, ${article.title}, KGOB J. (${monthShort}. ${day}, ${yr}).`,
    APA: `${lastFirst} (${yr}, ${monthLong} ${day}). ${article.title}. The KGOB Journal.`,
    Chicago: `${lastFirst}. "${article.title}." The KGOB Journal, ${monthLong} ${day}, ${yr}.`,
    MLA: `${lastFirst}. "${article.title}." The KGOB Journal, ${day} ${monthShort}. ${yr}.`,
  };
  const [copied, setCopied] = useState(false);
  function copy() {
    if (navigator.clipboard) navigator.clipboard.writeText(samples[fmt]).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }
  return (
    <div>
      <div className="cite-format">
        {formats.map((f) => (
          <button
            key={f}
            className={'cite-format-btn ' + (fmt === f ? 'active' : '')}
            onClick={() => setFmt(f)}
            data-testid={`journal-cite-format-${f.toLowerCase()}`}
          >
            {f}
          </button>
        ))}
      </div>
      <div
        style={{
          fontFamily: 'var(--serif)',
          fontStyle: 'italic',
          fontSize: 12,
          lineHeight: 1.5,
          color: 'rgba(246, 242, 234, 0.85)',
          background: 'rgba(196, 150, 88, 0.08)',
          padding: '12px 14px',
          marginTop: 12,
          borderLeft: '2px solid var(--brass)',
        }}
      >
        {samples[fmt]}
      </div>
      <button
        onClick={copy}
        style={{
          marginTop: 10,
          width: '100%',
          background: 'transparent',
          border: '1px solid rgba(196, 150, 88, 0.3)',
          color: copied ? 'var(--brass)' : 'var(--brass-soft)',
          fontFamily: 'var(--ui)',
          fontSize: 10,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          padding: '9px',
          cursor: 'pointer',
        }}
        data-testid="journal-cite-copy"
      >
        {copied ? '✓ Copied to clipboard' : 'Copy citation ↗'}
      </button>
    </div>
  );
}

// ---------- Read gate (email verification — uses universal subscription API) ----------
function ReadGate({ unlocked, onUnlock, articleUid }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [trap, setTrap] = useState('');
  const [error, setError] = useState('');
  const [shownAt] = useState(() => Date.now());
  const [verificationId, setVerificationId] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [issuedAt, setIssuedAt] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const DISPOSABLE = [
    'mailinator.com', 'tempmail.com', 'guerrillamail.com', '10minutemail.com',
    'throwaway.email', 'yopmail.com', 'trashmail.com', 'sharklasers.com',
    'getnada.com', 'maildrop.cc', 'fakeinbox.com', 'spamgourmet.com',
    'dispostable.com', 'emailondeck.com',
  ];

  if (unlocked) return null;

  function validate(value) {
    const re = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!re.test(value)) return 'Please enter a valid email address.';
    const domain = value.split('@')[1].toLowerCase();
    if (DISPOSABLE.indexOf(domain) >= 0) return "Disposable addresses aren't accepted. Please use your work email.";
    return null;
  }

  async function requestCode(e) {
    e.preventDefault();
    setError('');
    if (trap) return;
    if (Date.now() - shownAt < 2000) {
      setError('Hold on a moment, then try again.');
      return;
    }
    if (!consent) {
      setError("Please confirm you'd like to receive the Journal.");
      return;
    }
    const v = validate(email.trim());
    if (v) { setError(v); return; }

    setSubmitting(true);
    try {
      const { subscriptionsApi } = await import('@/lib/subscriptions');
      const { ok, data } = await subscriptionsApi.request({
        email: email.trim(),
        list_id: 'journal',
        first_name: null,
        source: 'journal_gate',
        source_article_uid: articleUid || null,
        website: trap,
      });
      if (!ok || !data.ok) {
        if (data.reason === 'rate_limit_ip' || data.reason === 'rate_limit_email') {
          setError("You've requested too many codes recently. Please wait 15 minutes.");
        } else if (data.reason === 'invalid_email') {
          setError('That email looks off — please double-check.');
        } else {
          setError('Something went wrong. Please try again.');
        }
        return;
      }
      if (data.already_subscribed) {
        // Already subscribed — unlock immediately
        onUnlock(email.trim());
        return;
      }
      setVerificationId(data.verification_id);
      setIssuedAt(Date.now());
      setStep('code');
      setEnteredCode('');
      setAttempts(0);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyCode(e) {
    e.preventDefault();
    setError('');
    if (Date.now() - issuedAt > 30 * 60 * 1000) {
      setError('Code expired. Request a new one.');
      setStep('email');
      return;
    }
    if (attempts >= 5) {
      setError('Too many attempts. Please start over.');
      setStep('email');
      return;
    }
    const code = enteredCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setSubmitting(true);
    try {
      const { subscriptionsApi } = await import('@/lib/subscriptions');
      const { ok, data } = await subscriptionsApi.verify({
        verification_id: verificationId,
        code,
      });
      if (!ok || !data.ok) {
        if (data.reason === 'code_mismatch') {
          setAttempts((a) => a + 1);
          setError("Code doesn't match. " + (4 - attempts) + ' attempts left.');
        } else if (data.reason === 'code_expired') {
          setError('Code expired. Request a new one.');
          setStep('email');
        } else if (data.reason === 'too_many_attempts') {
          setError('Too many attempts. Please request a new code.');
          setStep('email');
        } else {
          setError('Verification failed. Please try again.');
        }
        return;
      }
      // Persist token for future "already verified" detection
      try {
        if (data.master_token) {
          localStorage.setItem('kgob_subscriber_token', data.master_token);
        }
      } catch (_) { /* no action needed */ }
      onUnlock(email.trim());
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        margin: '40px -40px 0',
        padding: '40px 40px 36px',
        background: 'linear-gradient(180deg, transparent 0%, var(--paper) 18%, var(--cream) 100%)',
      }}
      data-testid="journal-read-gate"
    >
      <div
        style={{
          position: 'absolute',
          top: -120,
          left: 0,
          right: 0,
          height: 120,
          background: 'linear-gradient(180deg, transparent 0%, var(--paper) 100%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          background: 'var(--ink)',
          color: 'var(--cream)',
          padding: '32px 36px 30px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--ui)',
            fontSize: 10,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--brass-soft)',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ display: 'inline-block', width: 12, height: 1, background: 'var(--brass)' }} />
          {step === 'email' ? 'Continue reading · Free, verified sign-up' : 'Step 2 of 2 · Verify your email'}
        </div>

        {step === 'email' ? (
          <>
            <h3
              style={{
                fontFamily: 'var(--serif)',
                fontSize: 28,
                lineHeight: 1.15,
                fontWeight: 400,
                margin: '0 0 12px',
                textWrap: 'pretty',
              }}
            >
              You've read the first three minutes.
              <br />
              The rest is free. Verify your email to continue.
            </h3>
            <p
              style={{
                fontFamily: 'var(--ui)',
                fontSize: 13,
                lineHeight: 1.5,
                color: 'rgba(246, 242, 234, 0.7)',
                margin: '0 0 22px',
                maxWidth: 540,
              }}
            >
              We send a six-digit code to your inbox. Enter the code below to unlock the full essay and every other essay in the archive. The Journal arrives quarterly. Nothing else.
            </p>
            <form data-testid="journal-article-form-2" onSubmit={requestCode} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
              <input data-testid="journal-article-input-1"
                type="text"
                name="company"
                tabIndex="-1"
                autoComplete="off"
                value={trap}
                onChange={(e) => setTrap(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <div style={{ display: 'flex', gap: 0 }}>
                <input
                  type="email"
                  required
                  placeholder="your-email@firm.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  data-testid="journal-gate-email-input"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: '1px solid rgba(196, 150, 88, 0.4)',
                    borderRight: 'none',
                    padding: '13px 14px',
                    fontFamily: 'var(--ui)',
                    fontSize: 13,
                    color: 'var(--cream)',
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  data-testid="journal-gate-send-code"
                  style={{
                    background: 'var(--brass)',
                    color: 'var(--ink)',
                    fontFamily: 'var(--ui)',
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    border: '1px solid var(--brass)',
                    padding: '0 22px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Send code
                </button>
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontFamily: 'var(--ui)',
                  fontSize: 11,
                  color: 'rgba(246, 242, 234, 0.7)',
                  lineHeight: 1.4,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  style={{ marginTop: 3, accentColor: 'var(--brass)' }}
                  data-testid="journal-gate-consent"
                />
                <span>
                  Yes, send me the KGOB Journal. I can unsubscribe in one click. We never sell or share email addresses.
                </span>
              </label>
              {error && (
                <div
                  style={{
                    fontFamily: 'var(--ui)',
                    fontSize: 11,
                    color: '#E89B6C',
                    padding: '6px 0',
                    letterSpacing: '0.02em',
                  }}
                  data-testid="journal-gate-error"
                >
                  {error}
                </div>
              )}
            </form>
          </>
        ) : (
          <>
            <h3
              style={{
                fontFamily: 'var(--serif)',
                fontSize: 28,
                lineHeight: 1.15,
                fontWeight: 400,
                margin: '0 0 12px',
                textWrap: 'pretty',
              }}
            >
              Check your inbox.
            </h3>
            <p
              style={{
                fontFamily: 'var(--ui)',
                fontSize: 13,
                lineHeight: 1.5,
                color: 'rgba(246, 242, 234, 0.7)',
                margin: '0 0 18px',
                maxWidth: 540,
              }}
            >
              We just sent a six-digit code to{' '}
              <strong style={{ color: 'var(--cream)' }}>{email}</strong>. Enter it below to unlock the essay. Code expires in 10 minutes.
            </p>
            <form data-testid="journal-article-form-1" onSubmit={verifyCode} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
              <div style={{ display: 'flex', gap: 0 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength="6"
                  required
                  placeholder="6-digit code"
                  value={enteredCode}
                  onChange={(e) => {
                    setEnteredCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError('');
                  }}
                  data-testid="journal-gate-code-input"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: '1px solid rgba(196, 150, 88, 0.4)',
                    borderRight: 'none',
                    padding: '13px 14px',
                    fontFamily: 'var(--mono)',
                    fontSize: 16,
                    letterSpacing: '0.4em',
                    color: 'var(--cream)',
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  data-testid="journal-gate-verify"
                  style={{
                    background: 'var(--brass)',
                    color: 'var(--ink)',
                    fontFamily: 'var(--ui)',
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    border: '1px solid var(--brass)',
                    padding: '0 22px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Verify
                </button>
              </div>
              <div style={{ display: 'flex', gap: 16, fontFamily: 'var(--ui)', fontSize: 11 }}>
                <button data-testid="journal-article-button-6"
                  type="button"
                  onClick={() => setStep('email')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(246,242,234,0.55)',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Use a different email
                </button>
                <button data-testid="journal-article-button-5"
                  type="button"
                  onClick={() => {
                    if (submitting) return;
                    requestCode({ preventDefault: () => {} });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(246,242,234,0.55)',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Resend code
                </button>
              </div>
              {error && (
                <div
                  style={{
                    fontFamily: 'var(--ui)',
                    fontSize: 11,
                    color: '#E89B6C',
                    padding: '6px 0',
                    letterSpacing: '0.02em',
                  }}
                >
                  {error}
                </div>
              )}
            </form>
          </>
        )}

        <div
          style={{
            marginTop: 22,
            paddingTop: 16,
            borderTop: '1px solid rgba(196, 150, 88, 0.2)',
            fontFamily: 'var(--ui)',
            fontSize: 10,
            letterSpacing: '0.08em',
            color: 'rgba(246, 242, 234, 0.5)',
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <span>· Verified emails only</span>
          <span>· Disposable addresses blocked</span>
          <span>· Unsubscribe in one click</span>
          <span style={{ marginLeft: 'auto' }}>The KGOB Journal · Est. 2018</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Article body renderer ----------
function ArticleBody({ tldr, audio, body, unlocked, onUnlock, articleId }) {
  const gateIdx = (body || []).findIndex((b) => b.kind === 'gate');
  const visible = unlocked || gateIdx === -1 ? (body || []).filter((b) => b.kind !== 'gate') : body.slice(0, gateIdx);

  return (
    <div className="article-body">
      {tldr && (
        <div
          style={{
            background: 'var(--cream)',
            border: '1px solid var(--rule)',
            padding: '22px 26px',
            marginBottom: 36,
            borderLeft: '4px solid var(--brass)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--ui)',
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--brass)',
              marginBottom: 10,
            }}
          >
            TL;DR. Three takeaways
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontFamily: 'var(--serif)',
              fontSize: 17,
              lineHeight: 1.5,
              color: 'var(--ink)',
            }}
          >
            <li style={{ marginBottom: 8 }}>
              The opening paragraphs of this essay summarize the key changes.
            </li>
            <li style={{ marginBottom: 8 }}>
              The full citation list is in the right rail. Each citation links to primary sources.
            </li>
            <li>
              For client-specific implementation, contact the firm via the link at the bottom.
            </li>
          </ul>
        </div>
      )}
      {audio && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            background: 'var(--ink)',
            color: 'var(--cream)',
            marginBottom: 36,
          }}
        >
          <button data-testid="journal-article-button-4"
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: 'var(--brass)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--ink)',
            }}
          >
            <Icon.audio />
          </button>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'var(--ui)',
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--brass-soft)',
                marginBottom: 2,
              }}
            >
              Listen · 13:24
            </div>
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15 }}>
              Read by the editorial team
            </div>
          </div>
          <div
            style={{
              flex: 2,
              height: 28,
              background: 'rgba(196, 150, 88, 0.12)',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg viewBox="0 0 200 24" preserveAspectRatio="none" style={{ width: '100%', height: 20 }}>
              {Array.from({ length: 60 }).map((_, i) => {
                const h = 4 + Math.abs(Math.sin(i * 1.3)) * 18;
                return (
                  <rect
                    key={i}
                    x={i * 3.3}
                    y={(24 - h) / 2}
                    width="2"
                    height={h}
                    fill={i < 18 ? '#C49658' : 'rgba(246, 242, 234, 0.25)'}
                  />
                );
              })}
            </svg>
          </div>
        </div>
      )}

      {(visible || []).map((b, i) => {
        if (b.kind === 'lede') return <p key={i} className="lede" dangerouslySetInnerHTML={{ __html: b.text }} />;
        if (b.kind === 'p') return <p key={i} dangerouslySetInnerHTML={{ __html: b.text }} />;
        if (b.kind === 'h2') return <h2 key={i} id={b.id}>{b.text}</h2>;
        if (b.kind === 'pull') return <blockquote key={i} className="pull">{b.text}</blockquote>;
        if (b.kind === 'figure')
          return (
            <figure key={i} className="figure">
              <div className="figure-chart">
                <svg viewBox="0 0 600 280" preserveAspectRatio="none">
                  <g stroke="rgba(11, 34, 64, 0.08)" strokeWidth="1" fill="none">
                    {[55, 110, 165, 220].map((y) => (
                      <line key={y} x1="40" y1={y} x2="580" y2={y} />
                    ))}
                  </g>
                  <g fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(11, 34, 64, 0.45)" letterSpacing="0.04em">
                    <text x="40" y="265">YR 1</text>
                    <text x="180" y="265">YR 3</text>
                    <text x="320" y="265">YR 5</text>
                    <text x="460" y="265">YR 7</text>
                    <text x="555" y="265">YR 10</text>
                  </g>
                  <polyline points="40,235 120,220 200,200 280,180 360,160 440,140 520,100 570,40" fill="none" stroke="#A87437" strokeWidth="2.5" />
                  <polyline points="40,240 120,225 200,210 280,195 360,180 440,165 520,150 570,135" fill="none" stroke="#0B2240" strokeWidth="2" strokeDasharray="5 3" />
                  <polyline points="40,242 120,228 200,215 280,202 360,190 440,178 520,168 570,158" fill="none" stroke="#0B2240" strokeWidth="1.5" opacity="0.55" />
                  <g fontFamily="Geist, sans-serif" fontSize="10" fontWeight="500" letterSpacing="0.05em">
                    <circle cx="570" cy="40" r="3" fill="#A87437" />
                    <text x="560" y="32" textAnchor="end" fill="#A87437">CONDUIT TRUST</text>
                    <circle cx="570" cy="135" r="3" fill="#0B2240" />
                    <text x="560" y="127" textAnchor="end" fill="#0B2240">ACCUMULATION</text>
                    <circle cx="570" cy="158" r="3" fill="#0B2240" opacity="0.55" />
                    <text x="560" y="172" textAnchor="end" fill="#0B2240" opacity="0.65">MULTI-BENEFICIARY SEE-THROUGH</text>
                  </g>
                </svg>
              </div>
              <div className="cap">{b.caption}</div>
            </figure>
          );
        return null;
      })}

      {gateIdx !== -1 && <ReadGate unlocked={unlocked} onUnlock={onUnlock} articleUid={articleId} />}
    </div>
  );
}

// ---------- Main Article page ----------
export default function JournalArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = (ARTICLES || []).find((a) => a.id === id);

  const [tldr, setTldr] = useState(false);
  const [audio, setAudio] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [shareCount] = useState(247);
  const [progress, setProgress] = useState(0);
  const [hoveredCite, setHoveredCite] = useState(null);
  const [selectionShare, setSelectionShare] = useState(null);

  // Reader state, persisted in localStorage
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem('kgob_unlocked') === '1';
    } catch (e) {
      return false;
    }
  });
  const [readSet, setReadSet] = useState(() => {
    try {
      const v = localStorage.getItem('kgob_read');
      return new Set(v ? JSON.parse(v) : []);
    } catch (e) {
      return new Set();
    }
  });

  // Track this article as read
  useEffect(() => {
    if (!article) return;
    setReadSet((prev) => {
      const next = new Set(prev);
      next.add(article.id);
      try {
        localStorage.setItem('kgob_read', JSON.stringify([...next]));
      } catch (e) { /* no action needed */ }
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [article]);

  // Analytics: record a journal view 5 seconds after mount (filters bounces).
  // Sends to backend → MongoDB + bridge DB sync to app.kgob.com.
  useEffect(() => {
    if (!article || !article.uid) return;
    // Generate/retrieve anonymous session id (random, stored in localStorage, never sent as cookie)
    let sessionId;
    try {
      sessionId = localStorage.getItem('kgob_journal_sid');
      if (!sessionId) {
        sessionId = 'sess_' + Math.random().toString(36).slice(2, 14) + Date.now().toString(36).slice(-6);
        localStorage.setItem('kgob_journal_sid', sessionId);
      }
    } catch (e) {
      sessionId = 'sess_' + Math.random().toString(36).slice(2, 18);
    }

    const API = process.env.REACT_APP_BACKEND_URL;
    const timer = setTimeout(() => {
      // Only fire if the user is still on the page (engagement signal).
      if (document.visibilityState !== 'visible') return;
      try {
        fetch(`${API}/api/v1/journal/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            article_uid: article.uid,
            slug: article.id,
            title: article.title,
            category: article.category || null,
            author_name: article.author?.name || null,
            session_id: sessionId,
            referrer: (document.referrer || '').slice(0, 500),
          }),
          // Fire-and-forget; never block render. No await needed.
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        // Silently swallow — analytics must never break the page.
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [article]);

  // Reading-progress bar
  useEffect(() => {
    function onScroll() {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      const pct = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
      setProgress(pct);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [id]);

  // Citation hover preview
  useEffect(() => {
    function onHover(e) {
      const a = e.target.closest && e.target.closest('.cite');
      if (!a) {
        setHoveredCite(null);
        return;
      }
      const href = a.getAttribute('href') || '';
      const num = parseInt(href.replace('#cite-', ''), 10);
      if (!num) {
        setHoveredCite(null);
        return;
      }
      const rect = a.getBoundingClientRect();
      setHoveredCite({ num, x: rect.left, y: rect.bottom + 6 });
    }
    function onLeave(e) {
      if (!e.target.closest || !e.target.closest('.cite')) setHoveredCite(null);
    }
    document.addEventListener('mouseover', onHover);
    document.addEventListener('mouseout', onLeave);
    return () => {
      document.removeEventListener('mouseover', onHover);
      document.removeEventListener('mouseout', onLeave);
    };
  }, [id]);

  // Highlight-to-share
  useEffect(() => {
    function onSelect() {
      const sel = window.getSelection();
      const text = sel && sel.toString().trim();
      if (text && text.length > 12 && text.length < 280) {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setSelectionShare({ text, x: rect.left + rect.width / 2, y: rect.top - 8 });
      } else {
        setSelectionShare(null);
      }
    }
    document.addEventListener('selectionchange', onSelect);
    return () => document.removeEventListener('selectionchange', onSelect);
  }, [id]);

  function unlock(email) {
    setUnlocked(true);
    try {
      localStorage.setItem('kgob_unlocked', '1');
      localStorage.setItem('kgob_email', email);
    } catch (e) { /* no action needed */ }
    // Backend persistence is handled by ReadGate via subscriptionsApi.verify() —
    // subscriber, subscription, bridge mirror, and outbox event to app.kgob.com
    // are all written before this callback fires.
  }

  if (!article) {
    const alias = LEGACY_JOURNAL_ALIASES[id];
    if (alias) return <Navigate to={`/journal/${alias}`} replace />;
    return (
      <div className="kgob-journal-root">
        <Helmet>
          <title>Essay Not Found | The KGOB Journal</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <Masthead />
        <main className="page" data-testid="journal-not-found">
          <div className="archive-head">
            <h1>Essay not found.</h1>
            <p>
              The essay you're looking for has moved or never existed.{' '}
              <a data-testid="journal-article-a-3" onClick={() => navigate('/journal')} style={{ cursor: 'pointer', color: 'var(--brass)' }}>
                Return to the Journal index
              </a>
              .
            </p>
          </div>
        </main>
        <Colophon />
      </div>
    );
  }

  const hasBody = article.bodyKey && ARTICLE_BODIES[article.bodyKey];
  const body = hasBody
    ? ARTICLE_BODIES[article.bodyKey]
    : [
        { kind: 'lede', text: article.excerpt || article.deck },
        {
          kind: 'p',
          text: `This essay is part of <em>The Year-End Brief</em> from ${article.author.name.split(',')[0]}. The full analysis, including primary-source citations, will be published in the next edition of <em>The Journal</em>.`,
        },
        {
          kind: 'p',
          text: `If you need guidance on this topic for the current tax year, the firm is available for scoping calls. Contact KGOB at <a href="/contact" style="color:var(--brass)">kgob.com/contact</a> or reach the partner directly through the practice page.`,
        },
        { kind: 'gate' },
        {
          kind: 'p',
          text: `<em>Editor's note:</em> Long-form treatment of "${article.title}" is being prepared by ${article.author.name} and will appear in an upcoming issue. To receive notification when it publishes, subscribe to <em>The Journal</em> using the form above.`,
        },
      ];
  const citations = hasBody ? CITATIONS_BY_ARTICLE[article.bodyKey] || [] : [];
  const d = new Date(article.date);
  const dateStr = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const toc = (body || []).filter((b) => b.kind === 'h2');
  const related = (ARTICLES || []).filter(
    (a) => a.category === article.category && a.id !== article.id && a.bodyKey && ARTICLE_BODIES[a.bodyKey]
  );

  // ── Stacked JSON-LD: BlogPosting + FAQPage on every essay ──
  // Enrich the BlogPosting with extra fields Google's Article rich-result eligibility prefers
  // (dateModified, wordCount, articleBody snippet). Then stack a universal FAQPage so each
  // essay is also eligible for the FAQ rich-result accordion in search results.
  const bodyText = (body || [])
    .filter((b) => b.kind === 'p' || b.kind === 'lede')
    .map((b) => (b.text || '').replace(/<[^>]+>/g, ''))
    .join(' ');
  const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
  const articleBodySnippet = bodyText.slice(0, 500);

  const postSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `https://kgob.com/journal/${article.id}#post`,
        headline: article.title,
        description: article.deck,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Person',
          name: article.author.name,
          jobTitle: article.author.role,
          worksFor: { '@type': 'Organization', name: 'Kohari Gonzalez Oneyear & Brown PLLC' },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Kohari Gonzalez Oneyear & Brown PLLC',
          url: 'https://kgob.com',
          logo: { '@type': 'ImageObject', url: 'https://kgob.com/brand/kgob-logo.png', width: 1200, height: 284 },
        },
        image: { '@type': 'ImageObject', url: `https://kgob.com${article.image}`, width: 1200, height: 630 },
        url: `https://kgob.com/journal/${article.id}`,
        keywords: article.tags.join(', '),
        articleSection: article.category,
        wordCount,
        articleBody: articleBodySnippet,
        timeRequired: `PT${article.readMin || 8}M`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'Blog', '@id': 'https://kgob.com/journal#blog', name: 'The KGOB Journal' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://kgob.com/journal/${article.id}` },
      },
      {
        '@type': 'FAQPage',
        '@id': `https://kgob.com/journal/${article.id}#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How can I subscribe to The KGOB Journal?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The Journal is free to read. To receive new essays directly, verify your email through the gate that appears on any article — we send one quarterly digest plus occasional alerts on major tax-law changes. We do not sell or share email addresses, and you can unsubscribe in one click.',
            },
          },
          {
            '@type': 'Question',
            name: 'Who writes the essays in The KGOB Journal?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Essays are written by KGOB columnists and senior staff writers — Sara Gonzalez, CPA (Tax Strategy), Helena Park (Wealth Strategy), Priya Iyer (Tax Procedure), Marcus Reyes (Multistate Compliance), and Robbie (State of the States). Every essay is research-backed with primary-source citations to congress.gov, IRS publications, Federal Register notices, and state DOR materials.',
            },
          },
          {
            '@type': 'Question',
            name: 'How often does The Journal publish?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The Journal publishes essays year-round and rolls quarterly issues — The Winter Brief (Q1), The Spring Brief (Q2), The Summer Brief (Q3), and The Year-End Brief (Q4). New essays appear on the index page weekly during active tax legislation cycles.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can I cite a Journal essay in my own work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Every essay includes a citation block at the bottom with the recommended format (Bluebook, APA, Chicago, MLA). All primary sources are linked. For longer-form reprints, contact the firm — we are generally happy to grant permission for academic or professional use.',
            },
          },
          {
            '@type': 'Question',
            name: 'Are these essays legal or tax advice?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. The KGOB Journal is editorial commentary and educational analysis. It is research-backed and reflects how the firm thinks about current tax law, but it is not a substitute for engagement-level advice tailored to your specific situation. For implementation, contact KGOB at 1-844-599-3355 to schedule a scoping call.',
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="kgob-journal-root">
      <JsonLd id={`journal-post-${article.id}`} data={postSchema} />
      <Helmet>
        <title>{article.title} · The KGOB Journal</title>
        <meta name="description" content={article.deck} />
        <link rel="canonical" href={`https://kgob.com/journal/${article.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.deck} />
        <meta property="og:url" content={`https://kgob.com/journal/${article.id}`} />
        <meta property="og:image" content={`https://kgob.com${article.image}`} />
        <meta property="article:author" content={article.author.name} />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.category} />
        {(article.tags || []).map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@kgobcpa" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.deck} />
        <meta name="twitter:image" content={`https://kgob.com${article.image}`} />
        <link
          href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,300;1,6..72,400;1,6..72,500&family=Geist:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Masthead />

      <div className="article-page" data-testid={`journal-article-${article.id}`}>
        <div className="reading-progress" style={{ width: progress * 100 + '%' }} />
        <div className="article-grid">
          <ShareRail article={article} shareCount={shareCount} />

          <article className="article-main">
            <div className="article-breadcrumb">
              <span data-testid="journal-article-span-1" className="crumb" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>
                The Journal
              </span>
              <span className="sep">/</span>
              <span className="crumb article-cat">{article.category}</span>
              <span className="sep">/</span>
              <span>{article.year}</span>
            </div>

            <div className="article-head">
              <h1 className="article-title">{article.title}</h1>
              <p className="article-deck">{article.deck}</p>

              <div className="article-meta-row">
                <div className="article-byline">
                  <Avatar initials={article.author.initials} size={44} />
                  <div>
                    <div className="byline-name">{article.author.name}</div>
                    <div className="byline-role">{article.author.role}</div>
                  </div>
                </div>
                <div className="article-stamps">
                  <div className="stamp">
                    <span className="lbl">Published</span>
                    <span className="val">{dateStr}</span>
                  </div>
                  <div className="stamp">
                    <span className="lbl">Effective</span>
                    <span className="val">{article.effective}</span>
                  </div>
                  <div className="stamp">
                    <span className="lbl">Reading</span>
                    <span className="val">{article.readMin} min</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="article-toolbar">
              <button
                className={'tool-btn ' + (tldr ? 'on' : '')}
                onClick={() => setTldr(!tldr)}
                data-testid="journal-toolbar-tldr"
              >
                <Icon.tldr /> TL;DR
              </button>
              <button
                className={'tool-btn ' + (audio ? 'on' : '')}
                onClick={() => setAudio(!audio)}
                data-testid="journal-toolbar-listen"
              >
                <Icon.audio /> Listen
              </button>
              <button
                className={'tool-btn ' + (bookmarked ? 'on' : '')}
                onClick={() => setBookmarked(!bookmarked)}
                data-testid="journal-toolbar-save"
              >
                <Icon.bookmark /> {bookmarked ? 'Saved' : 'Save'}
              </button>
              <button className="tool-btn" onClick={() => window.print()} data-testid="journal-toolbar-print">
                <Icon.print /> Print / PDF
              </button>
              <span style={{ flex: 1 }}></span>
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  color: 'var(--ink-mute)',
                  letterSpacing: '0.06em',
                }}
              >
                ID · {article.id.toUpperCase()}
              </span>
            </div>

            <ArticleBody tldr={tldr} audio={audio} body={body} unlocked={unlocked} onUnlock={unlock} articleId={article?.id} />

            {unlocked && (
              <div className="end-of-article">
                <div className="end-byline">
                  <Avatar initials={article.author.initials} size={56} />
                  <div className="end-byline-text">
                    <h4>{article.author.name}</h4>
                    <p>{article.author.bio || article.author.role + ' at KGOB.'}</p>
                  </div>
                  <a
                    className="contact-btn"
                    onClick={() => navigate('/contact')}
                    style={{ cursor: 'pointer' }}
                    data-testid="journal-end-contact-btn"
                  >
                    Contact KGOB →
                  </a>
                </div>

                {related.length > 0 && (
                  <div className="further-reading">
                    <div className="label">Continue reading · Related from {article.category}</div>
                    <div className="further-grid">
                      {related.slice(0, 3).map((r) => (
                        <div
                          key={r.id}
                          className="fr-card"
                          onClick={() => navigate(`/journal/${r.id}`)}
                          data-testid={`journal-related-${r.id}`}
                        >
                          <div className="fr-cat">
                            {r.category} · {r.year}
                          </div>
                          <h4 className="fr-title">{r.title}</h4>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </article>

          <aside className="aside">
            <div className="aside-section">
              <div className="aside-title">In this essay</div>
              {toc.length > 0 ? (
                (toc || []).map((b, i) => (
                  <a data-testid="journal-article-a-2" key={i} className="toc-link" href={'#' + b.id}>
                    <span className="toc-n">{String(i + 1).padStart(2, '0')}</span> {b.text}
                  </a>
                ))
              ) : (
                <div style={{ fontFamily: 'var(--ui)', fontSize: 12, color: 'var(--ink-mute)' }}>
                  Headings appear after sign-up.
                </div>
              )}
            </div>

            <div className="aside-section dark">
              <div className="aside-title">
                Citations{' '}
                <span style={{ float: 'right', fontFamily: 'var(--mono)', color: 'var(--brass-soft)' }}>
                  ¶ {citations.length}
                </span>
              </div>
              {citations.map((c) => (
                <div key={c.n} className="citation" id={'cite-' + c.n}>
                  <span className="citation-num">{c.n}</span>
                  <div>
                    <div className="citation-source">{c.source}</div>
                    <div className="citation-note">{c.note}</div>
                    {c.url && c.url !== '#' && (
                      <a data-testid="journal-article-a-1"
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontFamily: 'var(--mono)',
                          fontSize: 10,
                          color: 'var(--brass-soft)',
                          letterSpacing: '0.04em',
                          textDecoration: 'none',
                          borderBottom: '1px dotted rgba(196, 150, 88, 0.4)',
                          paddingBottom: 1,
                          marginTop: 5,
                          display: 'inline-block',
                          wordBreak: 'break-all',
                        }}
                      >
                        {c.url.replace(/^https?:\/\//, '').slice(0, 44)}
                        {c.url.length > 51 ? '…' : ''} ↗
                      </a>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid rgba(196, 150, 88, 0.2)' }}>
                <div className="aside-title" style={{ marginBottom: 8 }}>
                  Cite this essay
                </div>
                <CitationFormatPanel article={article} />
              </div>
            </div>

            <div className="aside-section">
              <div className="aside-title">When you share this</div>
              <div className="share-preview">
                <div className="share-preview-image">
                  <div
                    className={'art art-' + article.hero}
                    style={{ position: 'absolute', inset: 0 }}
                  ></div>
                  <div className="share-preview-cat">{article.category}</div>
                </div>
                <div className="share-preview-body">
                  <div className="share-preview-title">{article.title}</div>
                  <div className="share-preview-deck">{article.deck}</div>
                  <div className="share-preview-foot">
                    <span className="share-preview-domain">kgob.com</span>
                    <span className="share-preview-sep">·</span>
                    <span>{dateStr}</span>
                    <span className="share-preview-sep">·</span>
                    <span>{article.readMin} min read</span>
                  </div>
                </div>
              </div>
              <p
                style={{
                  fontFamily: 'var(--ui)',
                  fontSize: 11,
                  color: 'var(--ink-mute)',
                  marginTop: 12,
                  lineHeight: 1.5,
                }}
              >
                This is how the article appears when shared on LinkedIn, X, Reddit, and via direct link. Open Graph tags carry the title, deck, hero image, byline, and published date.
              </p>
            </div>

            <div className="aside-section">
              <div className="aside-title">Tagged</div>
              <div className="tag-list">
                {(article.tags || []).map((t) => (
                  <span key={t} className="tag">#{t}</span>
                ))}
              </div>
              <div className="aside-title" style={{ marginTop: 22 }}>Permalink</div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  color: 'var(--ink-soft)',
                  wordBreak: 'break-all',
                  lineHeight: 1.5,
                  padding: '8px 10px',
                  background: 'var(--cream)',
                  border: '1px solid var(--rule-soft)',
                }}
              >
                kgob.com/journal/{article.id}
              </div>
            </div>
          </aside>
        </div>

        {hoveredCite &&
          (() => {
            const c = citations.find((x) => x.n === hoveredCite.num);
            if (!c) return null;
            return (
              <div className="cite-tooltip" style={{ left: hoveredCite.x, top: hoveredCite.y }}>
                <div className="cite-tooltip-num">¶ {c.n}</div>
                <div className="cite-tooltip-source">{c.source}</div>
                <div className="cite-tooltip-note">{c.note}</div>
              </div>
            );
          })()}

        {selectionShare && (
          <div
            className="selection-share"
            style={{ left: selectionShare.x, top: selectionShare.y }}
            data-testid="journal-selection-share"
          >
            <button data-testid="journal-article-button-3"
              onClick={() => {
                const url = `https://kgob.com/journal/${article.id}`;
                const text = `"${selectionShare.text}" — ${article.author.name.split(',')[0]}, ${article.title}`;
                if (navigator.clipboard) navigator.clipboard.writeText(text + '\n' + url).catch(() => {});
                setSelectionShare(null);
              }}
            >
              Copy quote
            </button>
            <span className="selection-share-sep">·</span>
            <button data-testid="journal-article-button-2"
              onClick={() => {
                const tweet = encodeURIComponent(`"${selectionShare.text}" via @kgobcpa`);
                const u = encodeURIComponent(`https://kgob.com/journal/${article.id}`);
                window.open(`https://twitter.com/intent/tweet?text=${tweet}&url=${u}`, '_blank');
              }}
            >
              Share on X
            </button>
            <span className="selection-share-sep">·</span>
            <button data-testid="journal-article-button-1"
              onClick={() => {
                const u = encodeURIComponent(`https://kgob.com/journal/${article.id}`);
                const title = encodeURIComponent(article.title);
                const summary = encodeURIComponent(selectionShare.text);
                window.open(
                  `https://www.linkedin.com/sharing/share-offsite/?url=${u}&title=${title}&summary=${summary}`,
                  '_blank'
                );
              }}
            >
              Share on LinkedIn
            </button>
          </div>
        )}
      </div>

      <Colophon />
    </div>
  );
}
